import React, { useState, useEffect } from 'react';
import '../styles/config-model.css';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../context/apiClient';

const ConfigModel = () => {
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedModelId, setSelectedModelId] = useState(null);
    const [modelName, setModelName] = useState('');
    const [modelVersion, setModelVersion] = useState('');
    const [modelAddress, setModelAddress] = useState('');
    const [apiToken, setApiToken] = useState('');
    const [modelId, setModelId] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [icon, setIcon] = useState('');
    const [isNewMode, setIsNewMode] = useState(false);
    const { user, logout } = useAuth();

    useEffect(() => {
        const fetchModels = async () => {
            if (!user?.token) return;
            try {
                const response = await authenticatedFetch('/api/config', {}, user.token, logout);
                if (!response.ok) {
                    throw new Error('Failed to fetch models');
                }
                const config = await response.json();
                if (config.models && config.models.length > 0) {
                    setModels(config.models);
                    setSelectedModel(config.models[0].name);
                    setSelectedModelId(config.models[0].id ?? null);
                    setIsNewMode(false);
                }
            } catch (e) {
                console.error('Fetch models error:', e);
            }
        };
        fetchModels();
    }, [user, logout]);

    useEffect(() => {
        if (isNewMode) {
            return;
        }
        const model = models.find(m => m.name === selectedModel);
        //if (model) {
            setSelectedModelId(model?.id ?? null);
            setModelName(model?.name || '');
            setModelVersion(model?.version || '');
            setModelAddress(model?.url || '');
            setApiToken(model?.token || '');
            setModelId(model?.model_id || '');
            setIcon(model?.icon || '');
            setSelectedFile(null);
            const fileInput = document.getElementById('file-upload-input');
            if (fileInput) fileInput.value = '';
        //}
    }, [selectedModel, models]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setIcon(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!modelName.trim()) {
            alert('Model Name is required.');
            return;
        }
        if (!modelId.trim()) {
            alert('Model ID is required.');
            return;
        }

        const payload = {
            name: modelName,
            version: modelVersion,
            url: modelAddress,
            token: apiToken,
            model_id: modelId,
            icon: icon,
        };

        try {
            let response;
            if (isNewMode) {
                // Add new model
                response = await authenticatedFetch('/api/models', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                }, user.token, logout);
            } else {
                if (!selectedModel) {
                    alert('Please select a model to update.');
                    return;
                }
                // Update existing model
                response = await authenticatedFetch(`/api/models/${encodeURIComponent(selectedModelId)}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                }, user.token, logout);
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to ${isNewMode ? 'add' : 'update'} model`);
            }

            alert(`Model ${isNewMode ? 'added' : 'updated'} successfully!`);
            // Refresh models list
            const fetchModelsResponse = await authenticatedFetch('/api/config', {}, user.token, logout);
            const config = await fetchModelsResponse.json();
            setModels(config.models || []);
            //if (!isNewMode) {
                setSelectedModel(modelName);
            //} else {
            //    setSelectedModel(config.models.length > 0 ? config.models[0].name : '');
            //}
            setIsNewMode(false);

        } catch (error) {
            console.error('Submit model error:', error);
            alert(error.message);
        }
    };

    const handleAddNew = () => {
        setIsNewMode(true);
        setSelectedModel('');
        setSelectedModelId(null);
        setModelName('');
        setModelVersion('');
        setModelAddress('');
        setApiToken('');
        setModelId('');
        setSelectedFile(null);
        // Optionally, reset the file input visually if needed
        const fileInput = document.getElementById('file-upload-input');
        if (fileInput) fileInput.value = '';
        setIcon('');
    };
    
    const handleCancel = () => {
        setIsNewMode(false);
        if (models.length > 0) {
            setSelectedModel(models[0].name);
        }
    };

    const handleDelete = async () => {
        if (!selectedModel) {
            alert('Please select a model to delete.');
            return;
        }

        if (window.confirm(`Are you sure you want to delete the model "${selectedModel}"?`)) {
            try {
                const response = await authenticatedFetch(`/api/models`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: selectedModelId }),
                }, user.token, logout);

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to delete model');
                }

                alert('Model deleted successfully!');
                
                // Refresh model list after deletion
                const fetchModelsResponse = await authenticatedFetch('/api/config', {}, user.token, logout);
                const config = await fetchModelsResponse.json();
                setModels(config.models || []);
                setSelectedModel(config.models.length > 0 ? config.models[0].name : '');
            } catch (error) {
                console.error('Delete model error:', error);
                alert(error.message);
            }
        }
    };

    return (
        <div className="config-model-page">
            <div className="container">
                <h1>Model Management</h1>

                <div className="layout-wrapper">
                    {/* Left Panel - Model List */}
                    <div className="models-list-panel">
                        <div className="list-header">
                            <h2>Models</h2>
                            <button type="button" className="btn-add-new" onClick={handleAddNew}>
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                                </svg>
                                Add New
                            </button>
                        </div>

                        <div className="models-list">
                            {isNewMode && (
                                <div className="model-card active new-model">
                                    <div className="model-icon-placeholder">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                                        </svg>
                                    </div>
                                    <div className="model-info">
                                        <h3>New Model</h3>
                                        <p>Fill in the details</p>
                                    </div>
                                </div>
                            )}
                            {models.map((model) => (
                                <div
                                    key={model.name}
                                    className={`model-card ${selectedModel === model.name && !isNewMode ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedModel(model.name);
                                        setSelectedModelId(model.id ?? null);
                                        setIsNewMode(false);
                                    }}
                                >
                                    <div className="model-icon">
                                        {model.icon ? (
                                            <img src={model.icon} alt={model.name} />
                                        ) : (
                                            <div className="model-icon-placeholder">
                                                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    <div className="model-info">
                                        <h3>{model.name}</h3>
                                        <p>{model.version || 'No version'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Panel - Model Details */}
                    <div className="model-details-panel">
                        {(selectedModel || isNewMode) ? (
                            <form className="details-form" onSubmit={handleSubmit}>
                                <div className="details-header">
                                    <h2>{isNewMode ? 'New Model' : 'Model Details'}</h2>
                                    {isNewMode && (
                                        <button type="button" className="btn-close" onClick={handleCancel}>
                                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>

                                <div className="form-content">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="model-name">Model Name</label>
                                            <input
                                                type="text"
                                                id="model-name"
                                                value={modelName}
                                                onChange={e => setModelName(e.target.value)}
                                                placeholder="Enter model name"
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="model-version">Model Parameter</label>
                                            <input
                                                type="text"
                                                id="model-version"
                                                value={modelVersion}
                                                onChange={e => setModelVersion(e.target.value)}
                                                placeholder="Enter model parameter"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="model-address">Model Request Address</label>
                                            <input
                                                type="text"
                                                id="model-address"
                                                value={modelAddress}
                                                onChange={e => setModelAddress(e.target.value)}
                                                placeholder="https://api.example.com/model"

                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="api-token">API Token</label>
                                            <input
                                                type="text"
                                                id="api-token"
                                                value={apiToken}
                                                onChange={e => setApiToken(e.target.value)}
                                                placeholder="Enter API token"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="model-id">Model ID</label>
                                            <input
                                                type="text"
                                                id="model-id"
                                                value={modelId}
                                                onChange={e => setModelId(e.target.value)}
                                                placeholder="Enter model ID"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="file-upload-input">Model Icon</label>
                                        <div className="file-upload">
                                            <input
                                                type="file"
                                                id="file-upload-input"
                                                onChange={handleFileChange}
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="file-upload-input" className="file-upload-label">
                                                {icon ? (
                                                    <img src={icon} alt="preview" className="icon-preview" />
                                                ) : (
                                                    <div className="upload-placeholder">
                                                        <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                        </svg>
                                                        <p className="upload-text">
                                                            {selectedFile ? selectedFile.name : 'Click to upload icon'}
                                                        </p>
                                                        <span className="upload-hint">PNG, JPG up to 2MB</span>
                                                    </div>
                                                )}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    {!isNewMode && (
                                        <button type="button" className="btn btn-danger" onClick={handleDelete}>
                                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                            </svg>
                                            Delete
                                        </button>
                                    )}
                                    <button type="submit" className="btn btn-primary">
                                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                        </svg>
                                        {isNewMode ? 'Create Model' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="empty-state">
                                <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                </svg>
                                <h3>No Model Selected</h3>
                                <p>Select a model from the list or create a new one</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfigModel;